import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CollaboratorFormModal } from '@/features/Collaborators/Edit/Form/Modais';
import { Collaborator, collaboratorSchemaHelpers } from '@/models';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { useUpdateCollaboratorMutation } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import {
    HiringFieldset,
    SignedContractsFieldset,
    BenefitsFieldset,
    ResponsibilitiesFieldset,
    NewSalaryEntryFieldset,
    SalaryHistoryFieldset,
    VacationFieldset
} from '@/features/Collaborators/Edit/Form/Fieldsets';

interface DpUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    collaboratorId: string | null;
}

interface DpUpdateModalContentProps {
    collaboratorId: string;
}

const DpUpdateModalContent: React.FC<DpUpdateModalContentProps> = ({ collaboratorId }) => {
    const {
        control,
        setValue,
        watch,
        formState: { errors, isSubmitting }
    } = useFormContext<CollaboratorFormData>();

    return (
        <div className="space-y-6">
            {/* HiringFieldset - Contratação */}
            <div className="-mt-6">
                <HiringFieldset
                    context="DP_UPDATE"
                    collaboratorId={collaboratorId}
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                    isSubmitting={isSubmitting}
                />
            </div>

            {/* SignedContractsFieldset - Contratos Assinados */}
            <SignedContractsFieldset
                context="DP_UPDATE"
                collaboratorId={collaboratorId}
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isSubmitting}
            />

            {/* ResponsibilitiesFieldset - Responsáveis */}
            <ResponsibilitiesFieldset
                context="DP_UPDATE"
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isSubmitting}
            />

            {/* BenefitsFieldset - Benefícios */}
            <BenefitsFieldset
                context="DP_UPDATE"
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isSubmitting}
            />

            {/* NewSalaryEntryFieldset - Adicionar Novo Registro Salarial */}
            <NewSalaryEntryFieldset
                context="DP_UPDATE"
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isSubmitting}
                collaboratorId={collaboratorId}
            />

            {/* SalaryHistoryFieldset - Histórico Salarial */}
            <SalaryHistoryFieldset
                context="DP_UPDATE"
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                isSubmitting={isSubmitting}
                collaboratorId={collaboratorId}
            />

            {/* VacationFieldset - Férias */}
            <VacationFieldset
                context="DP_UPDATE"
                collaboratorId={collaboratorId}
            />
        </div>
    );
};

const DpUpdateModal: React.FC<DpUpdateModalProps> = ({
    isOpen,
    onClose,
    collaboratorId
}) => {
    // Hooks para mutação e toast (igual ao DhoUpdateModal)
    const updateCollaboratorMutation = useUpdateCollaboratorMutation();
    const { success, error } = useToast();

    const handleSubmit = async (data: Collaborator) => {
        try {
            // 🔄 Configurar updateStatus específico do DP (igual ao DhoUpdateModal)
            const updatedData: Collaborator = {
                ...data,
                updatedStatus: {
                    ...data.updatedStatus,
                    byDP: {
                        status: 'UPDATED'
                    }
                }
            };

            // 🚀 Usar mutação real e toBackendData (igual ao DhoUpdateModal)
            await updateCollaboratorMutation.mutateAsync(
                collaboratorSchemaHelpers.toBackendData(updatedData as any)
            );

            // ✅ Toast de sucesso (igual ao DhoUpdateModal)
            success('Dados salvos!', 'Informações do DP atualizadas com sucesso');

        } catch (submitError) {
            console.error('💥 ERRO NO SUBMIT DP:', submitError);
            const errorMessage = submitError instanceof Error ? submitError.message : 'Erro desconhecido';
            error('Erro ao salvar dados', errorMessage);
            throw submitError;
        }
    };

    if (!isOpen || !collaboratorId) return null;

    // Fieldsets ativos no DP_UPDATE
    const currentFieldsets = [
        'HiringFieldSet',
        'SignedContractsFieldSet',
        'ResponsibilitiesFieldSet',
        'BenefitsFieldSet',
        'NewSalaryEntryFieldSet',
        'SalaryHistoryFieldSet',
        'VacationFieldSet'
    ];

    return (
        <CollaboratorFormModal
            isOpen={isOpen}
            onClose={onClose}
            context="DP_UPDATE"
            collaboratorId={collaboratorId}
            onSubmit={handleSubmit}
            title="Atualização DP"
            maxWidth="max-w-6xl"
            currentFieldsets={currentFieldsets}
        >
            <DpUpdateModalContent collaboratorId={collaboratorId} />
        </CollaboratorFormModal>
    );
};

export default DpUpdateModal; 
